import { Button, Icon, Link, Text, Tooltip } from "@shopify/polaris"
import { CheckSmallIcon } from "@shopify/polaris-icons"
import { ClipboardIcon, ViewIcon } from "polaris-enhance"
import { useEffect, useState } from "react"
import { Trans } from "react-i18next"
import { useHistory } from "react-router-dom"

import AddReturnPageModal, {
  checkMenuHasReturnPage,
} from "@/components/AddReturnPageModal"
import stores from "@/stores"
import { isShopifyEmbedded } from "@/utils/app-bridge"
import { customCopy } from "@/utils/copy"

interface BodyProps {
  completed: boolean
  onComplete: () => void
}

export function AddReturnPortalBody({ completed, onComplete }: BodyProps) {
  const { data, guideData } = stores.UserInfoStore
  const path = `https://${data.shop}/${data.prefix_path}/${data.path}`
  const trackURL = `/${data.prefix_path}/${data.path}`
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (completed) return
    // 仅当接口标记需要检查菜单时才检测
    if (!guideData.is_need_check_menu) return
    if (!isShopifyEmbedded()) return
    let cancelled = false
    checkMenuHasReturnPage(trackURL).then((found) => {
      if (cancelled) return
      // 菜单已包含退货页
      if (found) {
        // 告诉后端状态更新
        onComplete()
        stores.UserInfoStore.setGuideField("return_page_accessed", true)
      }
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-2">
      <Text as="p" variant="bodyMd" tone="subdued">
        {t("YourBrandedReturnPortalIsReady")}
      </Text>
      <div className="flex gap-2 items-center">
        <div className="flex-1 min-w-0 max-w-[455px]">
          <div className="rounded-lg bg-[var(--p-color-bg-surface-disabled)] px-3 py-1.5">
            <Text
              as="p"
              variant="bodyMd"
              tone="subdued"
              fontWeight="medium"
              truncate
            >
              {path}
            </Text>
          </div>
        </div>
        <Tooltip content={t("PreviewReturnPage")} dismissOnMouseOut>
          <div className="w-[28px]">
            <Button
              size="medium"
              icon={ViewIcon}
              onClick={() => window.open(path, "_blank")}
            />
          </div>
        </Tooltip>
        <Tooltip content={t("CopyLink")} dismissOnMouseOut>
          <div className="w-[28px]">
            <Button
              size="medium"
              icon={ClipboardIcon}
              onClick={() => customCopy(path)}
            />
          </div>
        </Tooltip>
      </div>
      <Text as="p" variant="bodyMd" tone="subdued">
        {t("AddItToYourStoreNavigationMenu")}
      </Text>
      <div className="flex items-center gap-2 pt-1">
        <Button
          variant={completed ? "secondary" : "primary"}
          onClick={() => setShow(true)}
        >
          {t("AddReturnPortal")}
        </Button>
        {!completed && (
          <Button variant="tertiary" onClick={onComplete}>
            {t("IveDoneThis")}
          </Button>
        )}
      </div>
      <AddReturnPageModal
        show={show}
        onClose={() => setShow(false)}
        onSuccess={() => {
          setShow(false)
          onComplete()
        }}
        trackURL={trackURL}
      />
    </div>
  )
}

export function EnableNotificationsBody({ completed, onComplete }: BodyProps) {
  const { push } = useHistory()
  return (
    <div className="space-y-3">
      <Text as="p" variant="bodyMd" tone="subdued">
        {t("CheckTheEmailsYourCustomersReceive")}
      </Text>
      <div>
        <Button
          variant={completed ? "secondary" : "primary"}
          onClick={() => {
            push("/settings/notification")
            if (!completed) onComplete()
          }}
        >
          {t("EnableNotifications")}
        </Button>
      </div>
    </div>
  )
}

export function TestReturnBody({ completed }: BodyProps) {
  const { push } = useHistory()
  const handleStart = () => {
    push("/return-management/test-order-management/exchange")
  }
  return (
    <div className="space-y-3">
      <Text as="p" variant="bodyMd" tone="subdued">
        {t("WalkThroughTheReturnFlowWithSampleData")}
      </Text>
      <div>
        <Button
          variant={completed ? "secondary" : "primary"}
          onClick={handleStart}
        >
          {t("StartTestReturn")}
        </Button>
      </div>
    </div>
  )
}

export function ReviewDefaultSettingsBody({
  completed,
  onComplete,
}: BodyProps) {
  const { push } = useHistory()
  return (
    <div className="space-y-3">
      <Text as="p" variant="bodyMd" tone="subdued">
        {t("WeveAppliedABasicSetupForYou")}
      </Text>
      <div>
        <Button
          variant={completed ? "secondary" : "primary"}
          onClick={() => {
            push("/settings")
            if (!completed) onComplete()
          }}
        >
          {t("ReviewSettings")}
        </Button>
      </div>
    </div>
  )
}

const COMPLETED_ONBOARDING_ITEMS = [
  "ReturnReasonsConfigured",
  "ReturnWindowSet",
  "ReturnSolutionsEnabled",
  "ReturnPortalCustomized",
]

export function CompletedDuringOnboardingList() {
  const { push } = useHistory()
  return (
    <div className="pb-2 space-y-1 px-2">
      <Text as="p" variant="bodyMd">
        <Trans i18nKey="YouCanReviewOrUpdateThemAnytimeInSettings">
          <Link removeUnderline onClick={() => push("/settings")} />
        </Trans>
      </Text>
      <div className="space-y-0.5">
        {COMPLETED_ONBOARDING_ITEMS.map((label) => (
          <div key={label} className="flex items-center gap-1 justify-left">
            <span>
              <Icon source={CheckSmallIcon} tone="success" />
            </span>
            <Text as="span" variant="bodyMd">
              {t(label)}
            </Text>
          </div>
        ))}
      </div>
    </div>
  )
}
